import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, queryString } from '@/lib/api';
import type { PositionInput, PositionView, UpdatePositionInput } from '@shared/schemas/position';
import type { ActivityFilter, ListResponse } from '@shared/schemas/pagination';
import type {
  ServiceTypeInput,
  ServiceTypeView,
  UpdateServiceTypeInput,
} from '@shared/schemas/service-type';

/*
 * The rate card and the positions list. ROADMAP.md §3.3.
 *
 * Two small resources with the same shape, so one file. Each keeps its own root
 * key — a price edit has no reason to refetch the careers positions.
 */

export interface CatalogListParams {
  page: number;
  search: string;
  activity: ActivityFilter;
  /** Defaults to the server's 25. A dropdown over the whole list asks for more. */
  pageSize?: number;
}

const SERVICES = ['admin', 'services'] as const;
const POSITIONS = ['admin', 'positions'] as const;

function invalidate(client: QueryClient, key: readonly string[]): Promise<void> {
  return client.invalidateQueries({ queryKey: key });
}

// ---------------------------------------------------------------------------
// Services — the rate card
// ---------------------------------------------------------------------------

export function useServiceTypes(params: CatalogListParams) {
  return useQuery({
    queryKey: [...SERVICES, 'list', params],
    queryFn: () =>
      api.get<ListResponse<ServiceTypeView>>(
        `/api/admin/services${queryString({
          page: params.page,
          search: params.search,
          activity: params.activity,
          pageSize: params.pageSize,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

/**
 * The skills and positions the service form's matching pickers offer — 7.4.
 *
 * One request, because the form cannot be drawn until it has both and two round
 * trips is two chances to half-render it. The same shape as the employees form's
 * `form-options`.
 */
export function useServiceFormOptions() {
  return useQuery({
    queryKey: [...SERVICES, 'form-options'],
    queryFn: () =>
      api.get<{
        skills: { id: string; name: string; isActive: boolean }[];
        positions: { id: string; name: string }[];
      }>('/api/admin/services/form-options'),
  });
}

export function useCreateServiceType() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ServiceTypeInput) =>
      api.post<ServiceTypeView>('/api/admin/services', input),
    onSuccess: () => invalidate(client, SERVICES),
  });
}

export function useUpdateServiceType(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateServiceTypeInput) =>
      api.patch<ServiceTypeView>(`/api/admin/services/${id}`, input),
    onSuccess: () => invalidate(client, SERVICES),
  });
}

export function useSetServiceTypeActive(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (isActive: boolean) =>
      api.post<ServiceTypeView>(`/api/admin/services/${id}/${isActive ? 'reactivate' : 'retire'}`),
    onSuccess: () => invalidate(client, SERVICES),
  });
}

// ---------------------------------------------------------------------------
// Positions
// ---------------------------------------------------------------------------

export function usePositions(params: CatalogListParams) {
  return useQuery({
    queryKey: [...POSITIONS, 'list', params],
    queryFn: () =>
      api.get<ListResponse<PositionView>>(
        `/api/admin/positions${queryString({
          page: params.page,
          search: params.search,
          activity: params.activity,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useCreatePosition() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: PositionInput) => api.post<PositionView>('/api/admin/positions', input),
    onSuccess: () => invalidate(client, POSITIONS),
  });
}

export function useUpdatePosition(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePositionInput) =>
      api.patch<PositionView>(`/api/admin/positions/${id}`, input),
    onSuccess: () => invalidate(client, POSITIONS),
  });
}

export function useSetPositionActive(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (isActive: boolean) =>
      api.post<PositionView>(`/api/admin/positions/${id}/${isActive ? 'reactivate' : 'retire'}`),
    onSuccess: () => invalidate(client, POSITIONS),
  });
}
